import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate, useLocation, Link } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { signInWithTestCredentials } from '@/utils/testAuth';
import { Sparkles } from 'lucide-react';
import BrandLogo from '@/components/common/BrandLogo';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';

const ELECTRON_FIRST_BOOT_LOGIN_KEY = 'electron-first-boot-login-complete';

export default function AuthPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { signIn, signUpWithStatus, user, refreshSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as any)?.from as string | undefined;
  const isElectron = typeof navigator !== 'undefined' && navigator.userAgent.toLowerCase().includes('electron');
  const hasAuthRedirectToken = location.hash.includes('access_token');
  const postAuthDestination = isElectron ? '/start' : from || '/';
  const { toast } = useToast();

  const [showDataPolicyModal, setShowDataPolicyModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'signin' | 'signup'>('signin');
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [isPreparingElectronLogin, setIsPreparingElectronLogin] = useState(isElectron && !hasAuthRedirectToken);

  useEffect(() => {
    if (!isElectron || hasAuthRedirectToken) {
      setIsPreparingElectronLogin(false);
      return;
    }

    const hasCompletedFirstBootLogin = localStorage.getItem(ELECTRON_FIRST_BOOT_LOGIN_KEY) === 'true';
    if (hasCompletedFirstBootLogin) {
      setIsPreparingElectronLogin(false);
      return;
    }

    let cancelled = false;

    const clearCachedElectronSession = async () => {
      try {
        await supabase.auth.signOut({ scope: 'local' });
      } catch (error) {
        console.warn('Failed to clear cached Electron session on first boot:', error);
      } finally {
        if (!cancelled) {
          setIsPreparingElectronLogin(false);
        }
      }
    };

    void clearCachedElectronSession();

    return () => {
      cancelled = true;
    };
  }, [hasAuthRedirectToken, isElectron]);

  useEffect(() => {
    const handleAuthRedirect = async () => {
      if (location.hash && location.hash.includes('access_token')) {
        setIsLoading(true);
        try {
          await refreshSession();
          if (isElectron) {
            localStorage.setItem(ELECTRON_FIRST_BOOT_LOGIN_KEY, 'true');
          }
          toast({
            title: 'Authentication successful',
            description: 'You have been signed in',
          });
          navigate(postAuthDestination);
        } catch (error) {
          toast({
            title: 'Authentication error',
            description: 'Could not process login. Please try again.',
            variant: 'destructive',
          });
        } finally {
          setIsLoading(false);
        }
      }
    };
    handleAuthRedirect();
  }, [location, navigate, refreshSession, toast, postAuthDestination]);

  if (user && !isPreparingElectronLogin) {
    return <Navigate to={postAuthDestination} replace />;
  }

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await signIn(email, password);
      if (isElectron) {
        localStorage.setItem(ELECTRON_FIRST_BOOT_LOGIN_KEY, 'true');
      }
      navigate(postAuthDestination);
    } catch (error: any) {
      toast({
        title: 'Error signing in',
        description: error.message || 'Could not sign in. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const { isExistingUser } = await (signUpWithStatus
        ? signUpWithStatus(email, password, { full_name: fullName })
        : Promise.resolve({ isExistingUser: false }));

      if (isExistingUser) {
        toast({
          title: 'Email already registered',
          description:
            "This email is already associated with an account. We've sent you a sign-in/verification email. If you forgot your password, use 'Forgot password'.",
        });
        setActiveTab('signin');
      } else {
        toast({
          title: 'Account created',
          description: 'Please check your email to verify your account.',
        });
        setShowDataPolicyModal(true);
      }
    } catch (error: any) {
      toast({
        title: 'Error signing up',
        description: error.message || 'Could not create account. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleTestLogin = async () => {
    setIsLoading(true);
    try {
      const result = await signInWithTestCredentials();
      if (result.success) {
        if (isElectron) {
          localStorage.setItem(ELECTRON_FIRST_BOOT_LOGIN_KEY, 'true');
        }
        toast({
          title: 'Test login successful',
          description: 'You have been signed in with test credentials.',
        });
        navigate(postAuthDestination);
      } else {
        toast({
          title: 'Test login failed',
          description: result.error || 'Could not sign in with test credentials.',
          variant: 'destructive'
        });
      }
    } catch (error: any) {
      toast({
        title: 'Test login error',
        description: error.message || 'An unexpected error occurred.',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(forgotPasswordEmail, {
        redirectTo: window.location.origin + '/auth',
      });
      if (error) throw error;
      toast({
        title: 'Password reset email sent',
        description: 'Please check your email for instructions to reset your password.',
      });
      setShowForgotPasswordModal(false);
      setForgotPasswordEmail('');
    } catch (error: any) {
      toast({
        title: 'Error sending reset email',
        description: error.message || 'Could not send password reset email. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const foundersEndDate = new Date('2025-09-01T00:00:00Z');
  const now = new Date();
  const isFoundersActive = now < foundersEndDate;

  const dataPolicyContent = (
    <div className="prose prose-sm dark:prose-invert max-w-none text-left">
      <h2>Data Policy</h2>
      <p><strong>Effective Date:</strong> Jan 04, 2025</p>
      <h3>1. Data Sovereignty and Residency</h3>
      <p>All confidential and identifying data processed by mapleos.ai is stored exclusively in Canada. Our data centers are located in Eastern Canada, specifically in Ontario, Quebec, and New Brunswick.</p>
      <p>Embeddings and other proprietary data structures generated by mapleos.ai are hosted solely within Canadian facilities to ensure compliance with Canadian privacy laws and regulations.</p>
      <p>mapleos.ai does not transmit any identifying data to services or entities located outside of Canada, including the United States.</p>
      <h3>2. Patriot Act and U.S. Data Compliance</h3>
      <p>mapleos.ai acknowledges the implications of the United States Patriot Act and ensures that no data processed by our services is stored on or transmitted to U.S.-based infrastructure or service providers.</p>
      <h3>3. User Rights</h3>
      <p>Users have the right to access, correct, or delete their data stored by mapleos.ai. Requests can be submitted through our support portal or via contact below.</p>
      <p>By using mapleos.ai services, you agree to this Data Policy. We recommend reviewing it periodically for updates.</p>
      <p className="mt-4 text-xs">
        For the full policy, see our <Link to="/data" className="underline text-primary">Data Policy page</Link>.
      </p>
    </div>
  );

  const handleDataPolicyClose = () => {
    setShowDataPolicyModal(false);
    setActiveTab('signin');
    setFullName('');
    setEmail('');
    setPassword('');
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background p-4">
      <div className="mb-6 flex flex-col items-center">
        <BrandLogo size={44} />
      </div>
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold">Welcome</CardTitle>
          <CardDescription>Sign in to your account or create a new one</CardDescription>
        </CardHeader>

        {isFoundersActive && (
          <div className="mb-4 flex items-center rounded-lg border border-primary/20 bg-muted p-3 shadow-sm relative">
            <div className="absolute left-0 top-0 bottom-0 w-1.5 rounded-l-lg bg-gradient-to-b from-primary to-primary" />
            <div className="pl-5 flex items-center gap-3 w-full">
              <Sparkles className="h-6 w-6 text-primary animate-pulse flex-shrink-0" />
              <div className="flex-1">
                <div className="font-semibold text-primary">Founder's Program</div>
                <div className="text-xs text-muted-foreground">
                  Everyone who signs up before <b>September 1st, 2025</b> will join our exclusive Founder's Program!
                  <br />
                  <span className="font-medium">Enjoy discounts, early access to beta features, special rewards, and a unique Founder's badge in your profile.</span>
                </div>
              </div>
            </div>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'signin' | 'signup')} className="w-full">
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="signin">Sign In</TabsTrigger>
            <TabsTrigger value="signup">Sign Up</TabsTrigger>
          </TabsList>

          <TabsContent value="signin">
            <form onSubmit={handleSignIn}>
              <CardContent className="space-y-4 pt-4">
                <div className="space-y-2">
                  <label htmlFor="email" className="text-sm font-medium">Email</label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <label htmlFor="password" className="text-sm font-medium">Password</label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>
              </CardContent>

              <CardFooter className="flex flex-col space-y-2">
                <Button
                  type="submit"
                  className="w-full"
                  disabled={isLoading}
                >
                  {isLoading ? 'Signing in...' : 'Sign In'}
                </Button>
                <Button
                  type="button"
                  variant="link"
                  className="w-full text-sm text-muted-foreground"
                  onClick={() => setShowForgotPasswordModal(true)}
                >
                  Forgot password?
                </Button>
              </CardFooter>
            </form>
          </TabsContent>

          <TabsContent value="signup">
            <form onSubmit={handleSignUp}>
              <CardContent className="space-y-4 pt-4">
                <div className="space-y-2">
                  <label htmlFor="fullName" className="text-sm font-medium">Full Name</label>
                  <Input
                    id="fullName"
                    placeholder="John Doe"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <label htmlFor="emailSignup" className="text-sm font-medium">Email</label>
                  <Input
                    id="emailSignup"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <label htmlFor="passwordSignup" className="text-sm font-medium">Password</label>
                  <Input
                    id="passwordSignup"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>
              </CardContent>

              <CardFooter>
                <Button
                  type="submit"
                  className="w-full"
                  disabled={isLoading}
                >
                  {isLoading ? 'Creating account...' : 'Create Account'}
                </Button>
              </CardFooter>
            </form>
          </TabsContent>
        </Tabs>
      </Card>
      <div className="mt-6 text-center text-xs text-muted-foreground">
        By using mapleos, you agree to our{' '}
        <Link to="/eula" className="underline hover:text-foreground transition-colors">EULA</Link>
        {' '}
        and
        {' '}
        <Link to="/data" className="underline hover:text-foreground transition-colors">Data Policy</Link>.
      </div>

      <Dialog open={showDataPolicyModal} onOpenChange={handleDataPolicyClose}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Data Policy</DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto px-1 py-2">
            {dataPolicyContent}
          </div>
          <DialogFooter>
            <Button
              onClick={handleDataPolicyClose}
              className="w-full"
              autoFocus
            >
              I have read and understand the Data Policy
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showForgotPasswordModal} onOpenChange={setShowForgotPasswordModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Forgot Your Password?</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleForgotPassword}>
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="forgotPasswordEmail">Email</Label>
                <Input
                  id="forgotPasswordEmail"
                  type="email"
                  placeholder="your@email.com"
                  value={forgotPasswordEmail}
                  onChange={(e) => setForgotPasswordEmail(e.target.value)}
                  required
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? 'Sending...' : 'Send Reset Link'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}